import { createFileRoute, notFound } from "@tanstack/react-router";
import InventoryDetail from "@/pages/InventoryDetail";
import { buildProductJsonLd, buildBreadcrumbJsonLd } from "@/lib/unitJsonLd";
import CarmaxUnit from "@/components/carmax/CarmaxUnit";
import { useThemeLayout } from "@/hooks/useThemeLayout";
import { inventoryUnitQueryOptions } from "@/hooks/useInventory";
import { UnitLoadError, UnitNotFound } from "@/components/inventory/UnitRouteStatus";
import { optimizedImg } from "@/lib/imageUtils";

export const Route = createFileRoute("/rv-search/$slug")({
  // Prime the unit read on the server so the first HTML already contains the
  // title, price block and hero <img> — the LCP image is discovered by the
  // parser instead of after hydration.
  loader: async ({ context, params }) => {
    let missing = false;
    let loadedUnit;
    try {
      const unit = await context.queryClient.ensureQueryData(
        inventoryUnitQueryOptions(params.slug),
      );
      // Sold/removed units previously rendered "Unit not found" with a 200
      // status, which Search Console reports as a Soft 404.
      missing = !unit;
      loadedUnit = unit;
    } catch {
      // Non-fatal: the component re-queries on the client.
    }
    if (missing) throw notFound();
    return { unit: loadedUnit };
  },
  head: ({ params, loaderData }) => {
    // LCP image: same optimizedImg("detail") call the gallery's eager <img>
    // makes, so the preload href byte-matches the displayed photo.
    const firstPhoto = loaderData?.unit?.images?.[0];
    const lcpUrl = firstPhoto ? optimizedImg(firstPhoto, "detail") : "";
    return {
    meta: [
      { title: `${loaderData?.unit?.title ?? "RV Details"} | Rex & Sons RVs` },
      { name: "description", content: `View ${loaderData?.unit?.title ?? "this RV"} at Rex & Sons RVs in Castle Hayne, NC. Explore photos, specifications, pricing and availability.` },
      { property: "og:title", content: `${loaderData?.unit?.title ?? "RV Details"} | Rex & Sons RVs` },
      { property: "og:description", content: `Explore ${loaderData?.unit?.title ?? "this RV"}, photos, specifications and pricing at Rex & Sons RVs.` },
      { name: "twitter:card", content: "summary_large_image" },
      {
        property: "og:url",
        content: `https://www.rexandsonsrvs.com/rv-search/${params.slug}`,
      },
      { property: "og:type", content: "product" },
      ...(lcpUrl
        ? [
            { property: "og:image", content: lcpUrl },
            { name: "twitter:image", content: lcpUrl },
          ]
        : []),
    ],
    links: [
      {
        rel: "canonical",
        href: `https://www.rexandsonsrvs.com/rv-search/${params.slug}`,
      },
      ...(lcpUrl
        ? [
            {
              rel: "preload",
              as: "image",
              href: lcpUrl,
              fetchpriority: "high",
            } as const,
          ]
        : []),
    ],
    // Rich-result schema lives in the route so every theme layout (Classic
    // and CarMax) ships it in the server HTML.
    scripts: loaderData?.unit
      ? [
          { type: "application/ld+json", children: JSON.stringify(buildProductJsonLd(loaderData.unit, `https://www.rexandsonsrvs.com/rv-search/${params.slug}`)) },
          { type: "application/ld+json", children: JSON.stringify(buildBreadcrumbJsonLd(loaderData.unit, `https://www.rexandsonsrvs.com/rv-search/${params.slug}`)) },
        ]
      : [],
    };
  },
  component: UnitRoute,
  errorComponent: UnitLoadError,
  notFoundComponent: UnitNotFound,
});

// Renders Classic by default; swaps to the theme's layout (admin preview or
// an activated theme) after mount. SSR always serves Classic.
function UnitRoute() {
  const layout = useThemeLayout();
  return layout === "carmax" ? <CarmaxUnit /> : <InventoryDetail />;
}
